/* src/contexts/ProfileContext.js */
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef
} from 'react';
import { doc, updateDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db as firestoreDb } from '../firebaseConfig'; // Import db instance

// Existing Internal Imports
import { ProfileContext, useProfile } from './profile/profileContextSetup';
import { useAuthStateListener } from './profile/profileAuth';
import { useProfileListLoader, useSubcollectionListeners } from './profile/profileDataFetching';
import { sanitizeTag } from './profile/profileUtils'; // Kept for actions remaining inside
import { LOCAL_STORAGE_KEY, DEFAULT_PROFILE_DATA } from './profile/profileConstants'; // Import constants

// Cloud Function Imports (Used by action creators)
import {
    deleteProfileAndCleanupFn, sendFriendRequestFn, acceptFriendRequestFn,
    handleFriendRequestFn, removeFriendFn, searchUsersFn,
    setProfileUsernameFn, checkSubscriptionStatusFn,
    fetchUserSessionsFn, exportUserSessionsFn
} from './profile/profileCloudFunctions';

// Action Hook Creator Imports
import {
    useCreateAddProfileAction,
    useCreateSetProfileUsernameAction,
    useCreateUpdateProfileDataAction,
    useCreateUpdateProfileProgressAction,
    useCreateRemoveProfileAction,
    useCreateMarkTutorialCompletedAction
} from './profile/profileActionsCRUD';
import {
    useCreateAddJournalTagAction,
    useCreateAddJournalEntryAction,
    useCreateUpdateJournalEntryAction,
    useCreateRemoveJournalEntryAction
} from './profile/profileActionsJournal';
import { useCreateAddCstResultAction } from './profile/profileActionsCST';
import {
    useCreateSendFriendRequestAction,
    useCreateAcceptFriendRequestAction,
    useCreateRejectFriendRequestAction,
    useCreateCancelFriendRequestAction,
    useCreateRemoveFriendAction,
    useCreateSearchUsersAction
} from './profile/profileActionsFriends';
import {
    useCreateFetchPaginatedSessionsAction,
    useCreateFetchAllSessionsForExportAction
} from './profile/profileActionsSessions';
import { useCreateCheckSubscriptionAction } from './profile/profileActionsSubscription';
import { useCreateMarkOnboardingCompleteAction } from './profile/profileActionsOnboarding';


// Re-export useProfile hook for convenience
export { useProfile };

// Helper function used in useMemo to provide a default structure if no profile is found
const getDefaultProfileStructure = () => ({
    ...DEFAULT_PROFILE_DATA,
    id: null,
    username: '', // Add username back here for default structure
    // --- <<< ADDED: Ensure defaults for new fields exist >>> ---
    highestNBackGood: null,
    highestAchievedGoodSessionId: null,
});

// --- ProfileProvider Component ---
export const ProfileProvider = ({ children }) => {
    // --- State Definitions ---
    const [profiles, setProfiles] = useState([]);
    const [activeProfileIdState, setActiveProfileIdState] = useState(() =>
        typeof window !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEY) : null
    );
    const [activeSessions, setActiveSessions] = useState([]);
    const [activeJournal, setActiveJournal] = useState([]);
    const [activeFriends, setActiveFriends] = useState([]);
    const [activeRequestsReceived, setActiveRequestsReceived] = useState([]);
    const [activeRequestsSent, setActiveRequestsSent] = useState([]);
    const [error, setError] = useState(null);
    const [userSubscriptionStatus, setUserSubscriptionStatus] = useState(null); // Initial state should be null or 'loading'
    const [userSubscriptionEndDate, setUserSubscriptionEndDate] = useState(null);
    const [userSubscriptionPriceId, setUserSubscriptionPriceId] = useState(null);
    const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true); // Start as true
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [userHadTrial, setUserHadTrial] = useState(false); // <<<--- ADDED: State for user's trial history

    // --- Custom Hooks ---
    const { authUser, isAuthResolved } = useAuthStateListener();
    const authUserRef = useRef(authUser);
    useEffect(() => { authUserRef.current = authUser; }, [authUser]);
    const userId = authUser?.uid || null;

    // --- Data Fetching Hooks ---
    const {
        profiles: loadedProfiles,
        isLoadingProfiles,
        profilesError,
        determinedActiveProfileId,
    } = useProfileListLoader(userId, !isAuthResolved);

    const {
        activeSessions: loadedSessions,
        activeJournal: loadedJournal,
        activeFriends: loadedFriends,
        activeRequestsReceived: loadedReceived,
        activeRequestsSent: loadedSent,
        isLoadingActiveSubcollections,
        subcollectionError: profileSubcollectionError,
    } = useSubcollectionListeners(userId, activeProfileIdState, isLoadingProfiles);

    // --- CORE ACTIONS (Kept inside or created from simple hooks) ---
    const selectProfile = useCallback(async (profileId) => {
        if (profileId === activeProfileIdState) return;

        const currentUserId = authUserRef.current?.uid;
        console.log(`[ProfileContext selectProfile] Attempting to select profile: ${profileId} for user: ${currentUserId}`);

        setActiveProfileIdState(profileId);
        try {
            if (profileId && typeof window !== 'undefined') {
                localStorage.setItem(LOCAL_STORAGE_KEY, profileId);
            } else if (typeof window !== 'undefined') {
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        } catch (storageError) {
            console.error("[ProfileContext selectProfile] Error accessing localStorage:", storageError);
        }

        if (currentUserId) {
             const userDocRef = doc(firestoreDb, 'users', currentUserId);
             try {
                 await updateDoc(userDocRef, { activeProfileId: profileId });
                 console.log(`[ProfileContext selectProfile] Successfully updated activeProfileId in Firestore for user ${currentUserId} to ${profileId}`);
                 setError(prevError => (prevError === "Failed to save active profile preference." ? null : prevError));
             } catch (firestoreError) {
                 console.error(`[ProfileContext selectProfile] Error updating activeProfileId in Firestore for user ${currentUserId}:`, firestoreError);
                 setError("Failed to save active profile preference.");
             }
        } else {
            console.warn("[ProfileContext selectProfile] No user logged in, skipping Firestore update.");
        }
        setActiveSessions([]); setActiveJournal([]); setActiveFriends([]); setActiveRequestsReceived([]); setActiveRequestsSent([]);
        setError(prevError => (prevError === profileSubcollectionError ? null : prevError));

    }, [activeProfileIdState, profileSubcollectionError, firestoreDb]); 

    // Onboarding action created from imported hook creator
    const markOnboardingComplete = useCreateMarkOnboardingCompleteAction(setIsOnboardingComplete);

    // --- CRUD ACTIONS (Created from imported hook creators) ---
    const addProfile = useCreateAddProfileAction(authUserRef, profiles, setError, setProfileUsernameFn);
    const setProfileUsername = useCreateSetProfileUsernameAction(authUserRef, setError, setProfileUsernameFn);
    const updateProfileData = useCreateUpdateProfileDataAction(authUserRef, setProfiles, setError);
    const updateProfileProgress = useCreateUpdateProfileProgressAction(authUserRef, setProfiles, setError);
    const markTutorialCompleted = useCreateMarkTutorialCompletedAction(authUserRef, setProfiles, setError);
    const removeProfile = useCreateRemoveProfileAction(authUserRef, activeProfileIdState, profiles, selectProfile, setError, deleteProfileAndCleanupFn);

    // --- JOURNAL ACTIONS (Created from imported hook creators) ---
    const addJournalTag = useCreateAddJournalTagAction(authUserRef, profiles, updateProfileData, setError);
    const addJournalEntry = useCreateAddJournalEntryAction(authUserRef, profiles, updateProfileData, setError);
    const updateJournalEntry = useCreateUpdateJournalEntryAction(authUserRef, profiles, updateProfileData, setError);
    const removeJournalEntry = useCreateRemoveJournalEntryAction(authUserRef, setError);

    // --- CST ACTIONS ---
    const addCstResult = useCreateAddCstResultAction(authUserRef, setError);

    // --- FRIEND ACTIONS ---
    const sendFriendRequest = useCreateSendFriendRequestAction(authUserRef, activeProfileIdState, setError, sendFriendRequestFn);
    const acceptFriendRequest = useCreateAcceptFriendRequestAction(authUserRef, activeProfileIdState, setError, acceptFriendRequestFn);
    const rejectFriendRequest = useCreateRejectFriendRequestAction(authUserRef, activeProfileIdState, setError, handleFriendRequestFn);
    const cancelFriendRequest = useCreateCancelFriendRequestAction(authUserRef, activeProfileIdState, setError, handleFriendRequestFn);
    const removeFriend = useCreateRemoveFriendAction(authUserRef, activeProfileIdState, setError, removeFriendFn);
    const searchUsers = useCreateSearchUsersAction(authUserRef, activeProfileIdState, setError, searchUsersFn);

    // --- SESSION FETCHING ACTIONS ---
    const fetchPaginatedSessions = useCreateFetchPaginatedSessionsAction(authUserRef, fetchUserSessionsFn);
    const fetchAllSessionsForExport = useCreateFetchAllSessionsForExportAction(authUserRef, exportUserSessionsFn);

    // --- SUBSCRIPTION ACTION ---
    const checkSubscription = useCreateCheckSubscriptionAction(
        authUserRef, setIsSubscriptionLoading, setUserSubscriptionStatus,
        setUserSubscriptionEndDate, setUserSubscriptionPriceId, setSubscriptionError,
        checkSubscriptionStatusFn
    );


    // --- Effect Hooks (Syncing state) ---
    const prevUid = useRef(undefined);
    useEffect(() => {
        if (!isAuthResolved) return;
        const current = authUser?.uid; const previous = prevUid.current;
        if (previous !== undefined && previous !== current) {
            console.log(`[Auth State Change] User changed from ${previous} to ${current}. Resetting profile state.`);
            setProfiles([]); setActiveProfileIdState(null); try { localStorage.removeItem(LOCAL_STORAGE_KEY); } catch {}
            setActiveSessions([]); setActiveJournal([]); setActiveFriends([]); setActiveRequestsReceived([]); setActiveRequestsSent([]);
            setUserSubscriptionStatus(null); setUserSubscriptionEndDate(null); setUserSubscriptionPriceId(null);
            setSubscriptionError(null);
            setIsSubscriptionLoading(true);
            setError(null); setIsOnboardingComplete(false);
            setUserHadTrial(false); // <<<--- RESET userHadTrial on auth change
        }
        prevUid.current = current;
    }, [isAuthResolved, authUser]);


    // Sync local profiles state with data from the listener hook
    useEffect(() => {
        if (!isLoadingProfiles && JSON.stringify(loadedProfiles) !== JSON.stringify(profiles)) {
             setProfiles(loadedProfiles);
        }
     }, [loadedProfiles, isLoadingProfiles, profiles]);

    // Auto-select profile based on listener determination and auth state
     useEffect(() => {
         const currentAuthUserId = authUserRef.current?.uid;
         if (!isAuthResolved || isLoadingProfiles) return;
         const profilesAvailable = Array.isArray(profiles) && profiles.length > 0;
         const isCurrentSelectionValid = profilesAvailable && activeProfileIdState && profiles.some(p => p.id === activeProfileIdState);

         if (currentAuthUserId) {
             if (!isOnboardingComplete) {
                 if (!isCurrentSelectionValid) {
                      if (determinedActiveProfileId !== activeProfileIdState) {
                          console.log(`[Auto-Select Effect] Onboarding NOT complete and Current selection invalid/null. Selecting loader's determined ID (${determinedActiveProfileId}).`);
                          selectProfile(determinedActiveProfileId);
                     }
                 }
             } else {
                 if (!isCurrentSelectionValid && profilesAvailable) {
                     const fallbackId = determinedActiveProfileId || profiles[0].id;
                     if (fallbackId !== activeProfileIdState) {
                         console.log(`[Auto-Select Effect] Onboarding complete, but selection invalid. Selecting fallback ID: ${fallbackId}`);
                         selectProfile(fallbackId);
                     }
                 }
             }
         } else {
             if (activeProfileIdState !== null) {
                 console.log("[Auto-Select Effect] User logged out. Triggering deselect.");
                 selectProfile(null);
             }
             if (isOnboardingComplete) {
                 console.log("[Auto-Select Effect] User logged out. Resetting onboarding complete flag.");
                 setIsOnboardingComplete(false);
             }
         }
    }, [
        isAuthResolved, isLoadingProfiles, authUser, determinedActiveProfileId,
        activeProfileIdState, profiles, selectProfile,
        isOnboardingComplete
    ]);


    // Sync subcollection states with loaded data
    useEffect(() => { setActiveSessions(loadedSessions); }, [loadedSessions]);
    useEffect(() => { setActiveJournal(loadedJournal); }, [loadedJournal]);
    useEffect(() => { setActiveFriends(loadedFriends); }, [loadedFriends]);
    useEffect(() => { setActiveRequestsReceived(loadedReceived); }, [loadedReceived]);
    useEffect(() => { setActiveRequestsSent(loadedSent); }, [loadedSent]);

    // Consolidate general error state from different loaders
    useEffect(() => {
        const generalError = profilesError || profileSubcollectionError || null;
        setError(prevError => {
            if (generalError === null && prevError !== null && prevError !== subscriptionError) {
                return null;
            } else if (generalError !== null && prevError !== generalError) {
                return generalError;
            }
            return prevError; // No change needed
        });
    }, [profilesError, profileSubcollectionError, subscriptionError]); // Include subscriptionError


    // Listener for User Document (Subscription Status AND Trial History)
    useEffect(() => {
        if (!isAuthResolved) { // If auth state is not yet resolved, reset and wait
            if (userSubscriptionStatus !== null || subscriptionError !== null || !isSubscriptionLoading || userHadTrial !== false) {
                setUserSubscriptionStatus(null); setUserSubscriptionEndDate(null); setUserSubscriptionPriceId(null); setSubscriptionError(null); setIsSubscriptionLoading(true);
                setUserHadTrial(false); // Reset trial status
            } return () => {};
        }

        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId) { // If auth resolved but no user, reset to non-user state
            if (userSubscriptionStatus !== null || subscriptionError !== null || isSubscriptionLoading || userHadTrial !== false) {
                 setUserSubscriptionStatus(null); setUserSubscriptionEndDate(null); setUserSubscriptionPriceId(null); setSubscriptionError(null); setIsSubscriptionLoading(false);
                 setUserHadTrial(false); // Reset trial status
            } return () => {};
        }

        // Auth resolved and user exists, set up listener
        let unsubscribeUserDoc = () => {};
        try {
            const userDocRef = doc(firestoreDb, 'users', currentUserId);
            console.log(`[User Listener] Setting up listener for user doc ${currentUserId}`);
            unsubscribeUserDoc = onSnapshot(userDocRef, (docSnap) => {
                // Ensure the snapshot belongs to the current user, in case of rapid auth changes
                if (currentUserId !== authUserRef.current?.uid) {
                    console.log(`[User Listener] Snapshot for ${currentUserId} ignored, current user is ${authUserRef.current?.uid}`);
                    return;
                }

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    console.log(`[User Listener] Data received for user ${currentUserId}:`, {status: data.subscriptionStatus, hasTrial: data.hasHadTrial});
                    const status = data.subscriptionStatus || 'inactive';
                    const endDateRaw = data.subscriptionCurrentPeriodEnd;
                    const priceId = data.subscriptionPriceId || null;
                    const endDate = endDateRaw instanceof Timestamp ? endDateRaw.toDate() : null;
                    const hadTrial = data.hasHadTrial === true; // Explicitly check for true

                    // Update states only if they changed to prevent re-renders
                    setUserSubscriptionStatus(prev => prev !== status ? status : prev);
                    setUserSubscriptionEndDate(prev => prev?.getTime() !== endDate?.getTime() ? endDate : prev);
                    setUserSubscriptionPriceId(prev => prev !== priceId ? priceId : prev);
                    setUserHadTrial(prev => prev !== hadTrial ? hadTrial : prev); // Update trial status

                    if(subscriptionError) setSubscriptionError(null); // Clear specific error on success
                } else {
                    console.log(`[User Listener] User document for ${currentUserId} does not exist.`);
                    setUserSubscriptionStatus(prev => prev !== 'no_user_doc' ? 'no_user_doc' : prev);
                    setUserSubscriptionEndDate(prev => prev !== null ? null : prev);
                    setUserSubscriptionPriceId(prev => prev !== null ? null : prev);
                    setUserHadTrial(prev => prev !== false ? false : prev); // Default to false if no doc
                    if(subscriptionError) setSubscriptionError(null);
                }
                 // Ensure loading is set to false AFTER the first successful read or determination
                 if (isSubscriptionLoading) setIsSubscriptionLoading(false);
            }, (error) => {
                if (currentUserId !== authUserRef.current?.uid) return; // Ignore if user changed
                console.error(`[User Listener] Error listening to user doc ${currentUserId}:`, error);
                setSubscriptionError("Listener error fetching subscription status.");
                setUserSubscriptionStatus('error');
                setUserSubscriptionEndDate(null); setUserSubscriptionPriceId(null);
                setUserHadTrial(false); // Default to false on error
                setIsSubscriptionLoading(false);
            });
        } catch (attachError) {
            console.error(`[User Listener] FATAL: Error attaching listener for ${currentUserId}:`, attachError);
            setError(prev => prev || "Critical error setting up subscription listener.");
            setSubscriptionError("Critical listener setup error.");
            setUserSubscriptionStatus('error');
            setUserHadTrial(false); // Default to false on critical error
            setIsSubscriptionLoading(false);
        }
        // Cleanup listener on unmount or when dependencies change
        return () => {
            console.log(`[User Listener] Cleaning up listener for user doc ${currentUserId}`);
            unsubscribeUserDoc();
        };
    }, [isAuthResolved, userId, firestoreDb]); // Removed isSubscriptionLoading, subscriptionError from deps to prevent loop


    // Trigger Subscription Check On Load/Login
    useEffect(() => {
        if (isAuthResolved && userId) { // User is authenticated
            // Only call checkSubscription if subscription data isn't already loaded by the listener
            // This helps prevent redundant calls if the listener fires quickly.
            if (isSubscriptionLoading && userSubscriptionStatus === null) {
                console.log(`[ProfileContext] Auth resolved for ${userId}. Triggering initial checkSubscription.`);
                checkSubscription();
            } else if (!isSubscriptionLoading) {
                console.log(`[ProfileContext] Auth resolved for ${userId}. Subscription data already loaded/loading by listener (Status: ${userSubscriptionStatus}, Loading: ${isSubscriptionLoading}). Skipping explicit checkSubscription call.`);
            }
        } else if (isAuthResolved && !userId) { // Auth resolved, no user
             console.log("[ProfileContext] Auth resolved, no user. Resetting subscription state.");
             setIsSubscriptionLoading(false);
             setUserSubscriptionStatus(null);
             setUserSubscriptionEndDate(null);
             setUserSubscriptionPriceId(null);
             setSubscriptionError(null);
             setUserHadTrial(false); // Reset trial status
        }
    }, [isAuthResolved, userId, checkSubscription, isSubscriptionLoading, userSubscriptionStatus]);


    // --- Derived State ---
    const activeProfileData = useMemo(() => {
        if (!activeProfileIdState || !Array.isArray(profiles) || profiles.length === 0) return getDefaultProfileStructure();
        const foundProfile = profiles.find(p => p.id === activeProfileIdState);
        if (!foundProfile) return getDefaultProfileStructure();
        return {
            ...getDefaultProfileStructure(), 
            ...foundProfile,                 
            id: foundProfile.id              
        };
    }, [activeProfileIdState, profiles]);

    const combinedActiveProfile = useMemo(() => {
         if (!activeProfileData || !activeProfileData.id) return null;
         return {
            ...activeProfileData,
            sessions: activeSessions,
            journal: activeJournal,
            friendsList: activeFriends,
            friendRequestsReceived: activeRequestsReceived,
            friendRequestsSent: activeRequestsSent,
         };
    }, [activeProfileData, activeSessions, activeJournal, activeFriends, activeRequestsReceived, activeRequestsSent]);

    // --- REFINED isLoading Calculation ---
    const isLoading = useMemo(() => {
        if (!isAuthResolved) return true; // Auth state not resolved yet
        if (!authUser) return false; // Auth resolved, no user, so main loading is done for app access
        // If user exists, then subscription, profiles, and subcollections matter
        if (isSubscriptionLoading) return true;
        if (isLoadingProfiles) return true;
        if (activeProfileIdState && isLoadingActiveSubcollections) return true; // Only check subcollections if a profile is active
        return false;
    }, [
        isAuthResolved, authUser, isSubscriptionLoading,
        isLoadingProfiles, activeProfileIdState, isLoadingActiveSubcollections,
    ]);


    // --- Final Context Value ---
    const value = useMemo(() => ({
        // Core State/Data
        authUser,
        isAuthLoading: !isAuthResolved, // More accurate naming
        profiles,
        activeProfileId: activeProfileIdState,
        activeProfile: combinedActiveProfile, 
        isLoading, // This is the overall loading state for guarded components
        error, // Consolidated general error
        userSubscriptionStatus, userSubscriptionEndDate, userSubscriptionPriceId,
        isSubscriptionLoading, // Specific subscription loading state
        subscriptionError, // Specific subscription error state
        userHadTrial, // <<<--- EXPOSE THE NEW STATE
        isOnboardingComplete,

        // Profile Actions (Internal + External Creators)
        selectProfile, 
        addProfile, setProfileUsername, removeProfile,
        updateProfileData, updateProfileProgress, 
        markTutorialCompleted, 

        // Journal Actions (Internal + External Creators)
        addJournalTag, 
        addJournalEntry, updateJournalEntry, removeJournalEntry,

        // CST Actions
        addCstResult,

        // Friend Actions
        sendFriendRequest, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, removeFriend, searchUsers,

        // Onboarding Action
        markOnboardingComplete,

        // Subscription Action
        checkSubscription,

        // Session Fetching Actions
        fetchPaginatedSessions,
        fetchAllSessionsForExport,

    }), [
        // State/Data Dependencies
        authUser, isAuthResolved, profiles, activeProfileIdState, combinedActiveProfile, 
        isLoading, error, userSubscriptionStatus, userSubscriptionEndDate, userSubscriptionPriceId,
        isSubscriptionLoading, subscriptionError, userHadTrial, isOnboardingComplete, // <<<--- ADD userHadTrial HERE
        // Action Dependencies
        selectProfile, addProfile, setProfileUsername, removeProfile,
        updateProfileData, updateProfileProgress, markTutorialCompleted,
        addJournalTag, addJournalEntry, updateJournalEntry, removeJournalEntry,
        addCstResult,
        sendFriendRequest, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, removeFriend, searchUsers,
        markOnboardingComplete, checkSubscription,
        fetchPaginatedSessions, fetchAllSessionsForExport,
    ]);

    // Provide the context value to children components
    return (
        <ProfileContext.Provider value={value}>
           <React.Fragment key={activeProfileIdState || 'no-profile'}>
                {children}
           </React.Fragment>
        </ProfileContext.Provider>
    );
};